import React from "react";
import { Users, Phone, MessageCircle, Edit } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_COLORS = {
  pending: "bg-gray-500/20 text-gray-300 border-gray-500/30",
  waiting: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  confirmed: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  arrived: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  partial: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  seated: "bg-green-500/20 text-green-300 border-green-500/30",
  completed: "bg-gray-600/20 text-gray-400 border-gray-600/30",
  cancelled: "bg-red-500/20 text-red-300 border-red-500/30",
  "no-show": "bg-red-700/20 text-red-400 border-red-700/30"
};

const STATUS_LABELS = {
  pending: "PENDING",
  waiting: "WAITING",
  confirmed: "CONFIRMED",
  arrived: "ARRIVED",
  partial: "PARTIAL",
  seated: "SEATED",
  completed: "COMPLETED",
  cancelled: "CANCELLED",
  "no-show": "NO SHOW"
};

const ReservationCard = ({ reservation, onClick, onStatusChange, compact = false }) => {
  const statusColor = STATUS_COLORS[reservation.status] || STATUS_COLORS.confirmed;
  const statusLabel = STATUS_LABELS[reservation.status] || reservation.status.toUpperCase();

  const handleStatusClick = (e) => {
    e.stopPropagation(); // Prevent card click when clicking status
  };

  if (compact) {
    return (
      <div
        onClick={() => onClick(reservation)}
        className={`
          relative px-3 py-1.5 rounded cursor-pointer
          bg-[#18181B] border border-[#27272A]
          hover:border-[#22C55E] hover:bg-[#27272A]/50
          transition-all duration-200
          min-w-[200px] max-w-[280px]
          ${statusColor.includes('green') ? 'border-l-4 border-l-green-500' : ''}
          ${statusColor.includes('blue') ? 'border-l-4 border-l-blue-500' : ''}
          ${statusColor.includes('red') ? 'border-l-4 border-l-red-500' : ''}
          ${statusColor.includes('yellow') ? 'border-l-4 border-l-yellow-500' : ''}
        `}
      >
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <p className="font-bold text-[#FAFAFA] text-sm truncate">
            {reservation.customer_name}
          </p>
          
          {/* Status Dropdown */}
          <div onClick={handleStatusClick} data-testid={`status-dropdown-wrapper-${reservation.booking_id}`}>
            <Select 
              value={reservation.status} 
              onValueChange={(value) => onStatusChange && onStatusChange(reservation, value)}
            >
              <SelectTrigger data-testid={`status-trigger-${reservation.booking_id}`} className={`${statusColor} text-[9px] font-mono px-1.5 h-5 border-0 whitespace-nowrap`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#18181B] border-[#27272A]">
                <SelectItem value="pending" className="text-[#FAFAFA] text-xs">PENDING</SelectItem>
                <SelectItem value="waiting" className="text-[#FAFAFA] text-xs">WAITING</SelectItem>
                <SelectItem value="confirmed" className="text-[#FAFAFA] text-xs">CONFIRMED</SelectItem>
                <SelectItem value="arrived" className="text-[#FAFAFA] text-xs">ARRIVED</SelectItem>
                <SelectItem value="partial" className="text-[#FAFAFA] text-xs">PARTIAL</SelectItem>
                <SelectItem value="seated" className="text-[#FAFAFA] text-xs">SEATED</SelectItem>
                <SelectItem value="completed" className="text-[#FAFAFA] text-xs">COMPLETED</SelectItem>
                <SelectItem value="cancelled" className="text-[#FAFAFA] text-xs">CANCELLED</SelectItem>
                <SelectItem value="no-show" className="text-[#FAFAFA] text-xs">NO SHOW</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex items-center gap-3 text-xs text-[#A1A1AA]">
          <div className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            <span className="font-semibold">{reservation.party_size}</span>
          </div>
          {reservation.phone && (
            <span className="truncate font-mono">{reservation.phone}</span>
          )}
        </div>
        
        {reservation.notes && (
          <div className="mt-1 flex items-center gap-1 text-[10px] text-[#A1A1AA]">
            <MessageCircle className="w-2.5 h-2.5 flex-shrink-0" />
            <span className="truncate">{reservation.notes}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={() => onClick(reservation)}
      className="relative p-3 rounded-lg cursor-pointer bg-[#18181B] border border-[#27272A] hover:border-[#22C55E] hover:bg-[#27272A]/50 transition-all duration-200"
    >
      {/* Edit Icon */}
      <div className="absolute top-2 right-2">
        <Edit className="w-4 h-4 text-[#A1A1AA] hover:text-[#22C55E]" />
      </div>

      {/* Customer Name */}
      <h3 className="font-bold text-[#FAFAFA] text-base mb-2 pr-6">
        {reservation.customer_name}
      </h3>

      {/* Info Row */}
      <div className="flex items-center gap-3 mb-2">
        <div className="flex items-center gap-1 text-sm text-[#A1A1AA]">
          <Users className="w-4 h-4" />
          <span>{reservation.party_size} PAX</span>
        </div>
        
        {reservation.phone && (
          <div className="flex items-center gap-1 text-sm text-[#A1A1AA]">
            <Phone className="w-4 h-4" />
            <span>{reservation.phone}</span>
          </div>
        )}
      </div>

      {/* Time */}
      <p className="text-xs text-[#A1A1AA] mb-2">
        {reservation.time}
      </p>

      {/* Status Badge */}
      <Badge className={`${statusColor} font-mono text-xs`}>
        {statusLabel}
      </Badge>

      {/* Notes */}
      {reservation.notes && (
        <div className="mt-2 pt-2 border-t border-[#27272A]">
          <div className="flex items-start gap-1">
            <MessageCircle className="w-3 h-3 text-[#A1A1AA] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-[#A1A1AA] line-clamp-2">{reservation.notes}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationCard;
